import { mdiClose, mdiContentCopy } from '@mdi/js';
import { Icon } from '@mdi/react';
import React, { forwardRef, useCallback, useId, useRef, useState } from 'react';
import { IconButton } from './Button/IconButton';
import { declareVariants } from '../lib/variantMatch';
import { cn } from '../lib/cn';

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> & {
	label?: string;
	description?: string;
	className?: string;
	invalid?: true | string;
	clearable?: boolean;
	showCopyButton?: boolean;
	onCopyClick?: (value: string) => void;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(({
	label,
	description,
	className,
	invalid,
	id: idProp,
	disabled,
	readOnly,
	clearable = true,
	showCopyButton = false,
	onCopyClick,
	onChange,
	value,
	defaultValue,
	...inputProps
}, ref) => {
	const uid = useId();
	const inputId = idProp ?? (label ? uid : undefined);
	const innerRef = useRef<HTMLInputElement>(null);
	const [uncontrolledLength, setUncontrolledLength] = useState(
		() => String(defaultValue ?? '').length,
	);
	const isControlled = value !== undefined;
	const hasValue = isControlled ? String(value ?? '').length > 0 : uncontrolledLength > 0;
	const isInvalid = Boolean(invalid);
	const supportingText = typeof invalid === 'string' ? invalid : description;

	const variants = declareVariants({
		disabled: Boolean(disabled),
		readOnly: Boolean(readOnly),
		invalid: Boolean(invalid),
	});

	const setRefs = useCallback((node: HTMLInputElement | null) => {
		innerRef.current = node;
		if (typeof ref === 'function') {
			ref(node);
		} else if (ref) {
			ref.current = node;
		}
	}, [ref]);

	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (!isControlled) {
			setUncontrolledLength(e.target.value.length);
		}
		onChange?.(e);
	}, [isControlled, onChange]);

	const handleClearClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		const el = innerRef.current;
		if (!el || disabled || readOnly) {
			return;
		}
		el.value = '';
		if (!isControlled) {
			setUncontrolledLength(0);
		}
		onChange?.({
			target: el,
			currentTarget: el,
		} as React.ChangeEvent<HTMLInputElement>);
	}, [disabled, readOnly, isControlled, onChange]);

	const handleCopyMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
	}, []);

	const handleCopyClick = useCallback(() => {
		if (onCopyClick) {
			onCopyClick(innerRef.current?.value);
		} else if (innerRef.current?.value) {
			innerRef.current?.select();
			navigator.clipboard.writeText(innerRef.current.value);
		}
	}, [onCopyClick]);

	return (
		<div className={cn('flex min-w-0 flex-col items-start gap-[5px]', className)}>
			{label ? (
				<label
					htmlFor={inputId}
					className="text-xs font-semibold leading-4 tracking-[0.24px] text-component-input-text-title"
				>
					{label}
				</label>
			) : null}
			<div
				className={cn(
					'group flex gap-1 w-full min-w-0 h-[40px] pr-4 items-center rounded-sm border border-component-input-border-default transition-[border-color,box-shadow,ring]',
					'bg-component-input-bg-default hover:border-component-input-border-hover hover:shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)]',
					'focus-within:border-component-input-border-typing focus-within:ring-2 focus-within:ring-component-input-border-focused focus-within:ring-offset-0',
					variants({
						readOnly: 'bg-grey-30 hover:border-component-input-border-default hover:shadow-none',
						disabled: 'border-component-input-border-disabled bg-component-input-bg-disabled hover:border-component-input-border-disabled hover:shadow-none focus-within:ring-0',
						invalid: 'border-component-input-border-error hover:border-component-input-border-error hover:shadow-none focus-within:border-component-input-border-error focus-within:ring-0',
					}),
				)}
			>
				<input
					ref={setRefs}
					id={inputId}
					disabled={disabled}
					readOnly={readOnly}
					value={isControlled ? value : undefined}
					defaultValue={isControlled ? undefined : defaultValue}
					onChange={handleInputChange}
					className={cn(
						'min-w-0 h-full flex-1 border-0 bg-transparent pl-3 outline-none',
						'text-sm font-normal leading-5 tracking-[0.14px]',
						'text-text-primary',
						'placeholder:text-component-input-text-placeholder_empty',
						'placeholder-shown:group-hover:placeholder:text-component-input-text-placeholder_empty_hover',
						'read-only:cursor-default read-only:text-component-input-text-placeholder_disabled',
						'disabled:cursor-not-allowed disabled:text-component-input-text-placeholder_disabled',
					)}
					{...inputProps}
				/>
				{clearable && !disabled && !readOnly && hasValue ? (
					<button
						type="button"
						tabIndex={-1}
						className={cn(
							'flex shrink-0 items-center justify-center rounded-sm p-0.5 text-fg-primary',
							'pointer-events-none opacity-0 transition-opacity',
							'group-hover:pointer-events-auto group-hover:opacity-100',
						)}
						onClick={handleClearClick}
					>
						<Icon path={mdiClose} className="size-[24px]" />
					</button>
				) : null}
				{showCopyButton && !disabled ? (
					<IconButton
						type="button"
						size="s"
						tooltip="Copy"
						icon={<Icon path={mdiContentCopy} />}
						className="shrink-0"
						onMouseDown={handleCopyMouseDown}
						onClick={handleCopyClick}
					/>
				) : null}
			</div>
			{supportingText ? (
				<p className={cn(
					'm-0 text-[11px] font-normal leading-[1.4] tracking-[0.11px]',
					isInvalid ? 'text-component-input-text-supporting-text_error' : 'text-component-input-text-title',
				)}>
					{supportingText}
				</p>
			) : null}
		</div>
	);
});
